import { FormInputState, FormModel, ResultValue } from 'src_common/common/mobx-utils/Form';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { validateExpectNumber, validateRequire } from 'src/api/utils/formValidators';
import { InputStatus } from 'src/components/Input/InputStatus';
import { EventModel } from 'src_common/common/websocket2/models/EventModel';
import { MarketModel } from 'src_common/common/websocket2/models/MarketModel';
import {
    isMarketFilter,
    isArray,
    MarketFilterType,
    MarketType,
} from 'src/AppState/CmsState/EventDetailsState/commonCmsGuard';
import { arrayMove } from 'react-movable';
import { ModelsState } from 'src_common/common/websocket2/ModelsState';
import { MobxMapAutoNew } from 'src_common/common/mobx-utils/MobxMapAutoNew';
import { FilterDetailsState } from './FilterDetailsState';
import { TypeId } from 'src_common/common/websocket2/type';

const parseFiltersMapToNewMarketId = (filtersMap: Map<string, MarketFilterType>): Map<string, MarketFilterType> => {
    const tempMap: Map<string, MarketFilterType> = new Map();
    for (const [key, filter] of Array.from(filtersMap)) {
        const tempMarkets = [];
        for (const market of filter.markets) {
            tempMarkets.push({
                ...market,
                id: market.id,
            });
        }

        tempMap.set(key, {
            ...filter,
            markets: tempMarkets,
        });
    }

    return tempMap;
};

export class MarketsFiltersState {
    public readonly selectedMarketFilterModel: FormModel<string>;
    public readonly selectedMarketFilter: FormInputState<string>;
    public readonly selectedMarketFilterStatus: InputStatus;

    public readonly anotherEventIdModel: FormModel<number | null>;
    public readonly anotherEventId: FormInputState<string>;
    public readonly anotherEventIdStatus: InputStatus;

    public readonly filterNameModel: FormModel<string>;
    public readonly filterName: FormInputState<string>;
    public readonly filterNameStatus: InputStatus;

    @observable public filtersMap: Map<string, MarketFilterType>;
    @observable public marketsFiltersMap: Map<string, Array<number>>;
    @observable public isMarketsFromCurrentEvent: string = 'current-event';
    @observable private anotherEventInnerId: number | null = null;
    @observable private isCheckEvent: boolean = false;
    @observable public activeFilter: string | null = null;
    @observable public activeEditFilter: string | null = null;

    private readonly filterDetailsMap: MobxMapAutoNew<string, FilterDetailsState>;

    public constructor(
        private readonly modelsState: ModelsState,
        private readonly eventId: number,
        editData: unknown | null
    ) {
        const filtersMap = this.decodeMarketFilterData(editData);
        this.filtersMap = parseFiltersMapToNewMarketId(filtersMap);
        this.marketsFiltersMap = new Map();
        this.selectedMarketFilter = new FormInputState('all-markets', false);
        this.selectedMarketFilterModel = this.selectedMarketFilter.toModel();
        this.selectedMarketFilterStatus = new InputStatus(this.selectedMarketFilterModel);

        this.anotherEventId = new FormInputState('', false);
        this.anotherEventIdModel = this.anotherEventId.map(validateExpectNumber);
        this.anotherEventIdStatus = new InputStatus(this.anotherEventIdModel);

        this.filterName = new FormInputState('', false);
        this.filterNameModel = this.filterName.map(validateRequire);
        this.filterNameStatus = new InputStatus(this.filterNameModel);
        this.filterDetailsMap = new MobxMapAutoNew(
            (filterSlug: string): FilterDetailsState => new FilterDetailsState(this.filtersMap.get(filterSlug) ?? null)
        );
        makeAutoObservable(this);
    }

    @action getFilterDetailsBySlug = (slug: string): FilterDetailsState => {
        return this.filterDetailsMap.get(slug);
    };

    decodeMarketFilterData = (editData: unknown | null): Map<string, MarketFilterType> => {
        const tempMap: Map<string, MarketFilterType> = new Map();
        if (editData !== null && isArray(editData)) {
            for (const elem of editData) {
                if (isMarketFilter(elem)) {
                    // @ts-expect-error
                    tempMap.set(elem.slug, elem);
                }
            }
        }

        return tempMap;
    };

    public getMarketById = (id: TypeId.MarketId): MarketModel | null => {
        return this.modelsState.getMarket(id);
    };

    @computed public get anotherEventIdValue(): number | null {
        const anotherEventId = this.anotherEventIdModel.result;

        if (anotherEventId instanceof ResultValue && anotherEventId.value !== null) {
            return anotherEventId.value;
        }
        return null;
    }

    @computed public get selectedMarketFilterValue(): string | null {
        const filter = this.selectedMarketFilterModel.result;
        if (filter instanceof ResultValue && filter.value !== null) {
            return filter.value;
        }

        return null;
    }

    @action public addMarketToFilter = (marketId: TypeId.MarketId): void => {
        if (this.activeFilter !== null) {
            const marketsFilterElem = this.filtersMap.get(this.activeFilter) ?? null;
            if (marketsFilterElem !== null) {
                const market = {
                    id: marketId,
                    displayOrder: this.filtersMap.size + 1,
                };

                this.filtersMap.set(this.activeFilter, {
                    ...marketsFilterElem,
                    markets: [...marketsFilterElem.markets, market],
                });
            }
        }
    };

    @action public removeMarketToFilter = (marketId: number): void => {
        if (this.activeFilter !== null) {
            const marketsFilterElem = this.marketsFiltersMap.get(this.activeFilter) ?? null;
            if (marketsFilterElem !== null) {
                const markets = marketsFilterElem.filter((elem) => elem !== marketId);
                this.marketsFiltersMap.set(this.activeFilter, markets);
            }
        }
    };

    @action public handleChangeMarketsTabToCurrent = (): void => {
        this.isMarketsFromCurrentEvent = 'current-event';
    };

    @action public handleChangeMarketsTabToAnother = (): void => {
        this.isMarketsFromCurrentEvent = 'another-event';
    };

    @action public handleAddFilter = (): void => {
        const result = this.filterNameModel.result;

        if (result instanceof ResultValue && result.value !== null) {
            const marketFiltersIds = Array.from(this.filtersMap.keys()).map((elem) => {
                const id = parseInt(elem.slice(14, elem.length), 10);
                if (isNaN(id)) {
                    return 0;
                }

                return id;
            });

            const id = marketFiltersIds.length > 0 ? Math.max(...marketFiltersIds) + 1 : 0;

            const slug = `market-filter-${id}`;

            this.filtersMap.set(slug, {
                slug: slug,
                label: result.value,
                displayOrder: this.filtersMap.size + 1,
                markets: [],
                description: '',
                starValues: [],
            });

            this.filterNameModel.reset();
        }
    };

    @computed public get allMarketsIds(): Array<TypeId.MarketId> {
        return this.modelsState.getEvent(this.eventId)?.marketsIds ?? [];
    }

    @computed public get addedMarketsIdsList(): Array<MarketType> {
        if (this.activeFilter !== null) {
            const marketsFilterElem = this.filtersMap.get(this.activeFilter) ?? null;
            return marketsFilterElem?.markets ?? [];
        }
        return [];
    }

    @action handleMarketsOrder = (marketList: Array<MarketType>, slug: string): void => {
        if (this.activeFilter !== null) {
            const filterItem = this.filtersMap.get(slug) ?? null;
            if (filterItem !== null) {
                const markets = marketList.map((market, index) => ({ ...market, displayOrder: index + 1 }));
                this.filtersMap.set(slug, { ...filterItem, markets });
            }
        }
    };

    @action handleFiltersOrder = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
        const filtersSlugList = arrayMove(this.filtersList, oldIndex, newIndex);

        const tempMap = new Map();
        const tempFilters = filtersSlugList.map((filter, index) => ({ ...filter, displayOrder: index + 1 }));
        for (const filterItem of tempFilters) {
            tempMap.set(filterItem.slug, filterItem);
        }

        this.filtersMap = tempMap;
    };

    @computed public get restMarketsIdsList(): Array<TypeId.MarketId> {
        return this.allMarketsIds.filter((id) => this.addedMarketsIdsList.some((elem) => elem.id === id) === false);
    }

    @computed public get anotherMarketsIdsList(): Array<TypeId.MarketId> {
        return this.anotherEventMarketsIdsListInner.filter(
            (id) => this.addedMarketsIdsList.some((elem) => elem.id === id) === false
        );
    }

    @computed public get checkEventStatus(): 'ok' | 'error' | 'default' {
        if (this.isCheckEvent && this.anotherEventModel !== null) {
            return 'ok';
        }
        if (this.isCheckEvent && this.anotherEventModel === null) {
            return 'error';
        }
        return 'default';
    }

    @computed public get checkEventLabel(): string {
        if (this.checkEventStatus === 'ok') {
            return 'Valid';
        }
        if (this.checkEventStatus === 'error') {
            return 'Invalid';
        }

        return 'Check';
    }

    @computed private get anotherEventMarketsIdsListInner(): Array<TypeId.MarketId> {
        if (this.anotherEventModel !== null) {
            return this.anotherEventModel.marketsIds;
        }
        return [];
    }

    @computed get anotherEventModel(): EventModel | null {
        if (this.anotherEventInnerId !== null) {
            return this.modelsState.getEvent(this.anotherEventInnerId);
        }

        return null;
    }

    @action public handleCheckEvent = async (): Promise<void> => {
        const anotherEventId = this.anotherEventIdModel.result;
        this.anotherEventInnerId = null;

        if (anotherEventId instanceof ResultValue && anotherEventId.value !== null) {
            this.anotherEventInnerId = anotherEventId.value;
        }
        this.isCheckEvent = true;
    };

    @computed public get filtersList(): Array<MarketFilterType> {
        return Array.from(this.filtersMap.values());
    }

    @computed public get selectedFiltersTagsSlugList(): Array<string> {
        return this.filtersList.map((elem) => elem.slug);
    }

    @action public onRemoveFilter = (slug: string): void => {
        this.filtersMap.delete(slug);
        this.marketsFiltersMap.delete(slug);

        if (this.activeFilter === slug) {
            this.activeFilter = null;
        }
    };

    @action public onEditFilter = (slug: string): void => {
        if (this.activeEditFilter === null) {
            this.activeFilter = slug;
            this.activeEditFilter = slug;
        }
    };

    @action public onCloseEditFilter = (): void => {
        this.activeEditFilter = null;
    };

    @action public onActiveFilter = (slug: string): void => {
        if (this.activeFilter === slug) {
            this.activeFilter = null;
        } else {
            this.activeFilter = slug;
        }
        this.activeEditFilter = null;
    };

    getFilterBySlug = (slug: string): MarketFilterType | null => {
        return this.filtersMap.get(slug) ?? null;
    };

    @action handleRemoveMarket = (slug: string, marketId: TypeId.MarketId): void => {
        const filterItem = this.filtersMap.get(slug) ?? null;
        if (filterItem !== null) {
            const filteredMarkets = filterItem.markets.filter((market) => {
                return market.id !== marketId;
            });
            this.filtersMap.set(slug, { ...filterItem, markets: filteredMarkets });
        }
    };

    @action onChangeEventCallBack = (): void => {
        this.anotherEventInnerId = null;
        this.isCheckEvent = false;
    };

    @computed get prepareData(): Array<MarketFilterType> {
        return Array.from(this.filtersMap.values()).map((elem) => {
            const filterDetails = this.getFilterDetailsBySlug(elem.slug);

            return {
                ...elem,
                description: filterDetails.starFieldData?.description ?? '',
                starValues: filterDetails.starFieldData?.starData ?? [],
            };
        });
    }
}
